import React from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';

const s = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1055,
        padding: '16px',
    },
    modal: {
        background: '#fff',
        borderRadius: '16px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '92vh',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 24px 16px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fff',
        flexShrink: 0,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    titleIcon: {
        width: '36px',
        height: '36px',
        borderRadius: '9px',
        background: 'linear-gradient(135deg, #1a7d3e 0%, #34a85a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(26,125,62,0.25)',
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '12px',
        color: '#9ca3af',
        marginTop: '1px',
    },
    btnClose: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#6b7280',
        transition: 'all 0.15s ease',
        flexShrink: 0,
    },
    body: {
        padding: '22px 24px',
        overflowY: 'auto',
        flex: 1,
        background: '#fafafa',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
        padding: '14px 24px',
        borderTop: '1px solid #e5e7eb',
        background: '#fff',
        flexShrink: 0,
    },
};

/**
 * Common modal shell used across the app.
 *
 * Props:
 *   show        {boolean}   – controls visibility
 *   onClose     {function}  – called when overlay or × is clicked
 *   icon        {ReactNode} – icon rendered inside the green gradient square
 *   title       {string}    – modal heading
 *   subtitle    {string}    – optional subtitle below heading
 *   maxWidth    {string}    – default '560px'
 *   children    {ReactNode} – body content
 *   footer      {ReactNode} – footer content (buttons); omit to hide footer
 */
const AppModal = ({ show, onClose, icon, title, subtitle, maxWidth = '560px', children, footer, staticBackdrop = false }) => {
    if (!show) return null;

    return ReactDOM.createPortal(
        <div
            style={s.overlay}
            onClick={(e) => { if (e.target === e.currentTarget && !staticBackdrop) onClose(); }}
        >
            <div style={{ ...s.modal, maxWidth }} onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div style={s.header}>
                    <div style={s.headerLeft}>
                        <div style={s.titleIcon}>{icon}</div>
                        <div style={s.titleBlock}>
                            <div style={s.title}>{title}</div>
                            {subtitle && <div style={s.subtitle}>{subtitle}</div>}
                        </div>
                    </div>
                    <button
                        style={s.btnClose}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.color = '#6b7280'; }}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FiX size={16} />
                    </button>
                </div>

                {/* Body */}
                <div style={s.body}>{children}</div>

                {/* Footer */}
                {footer && <div style={s.footer}>{footer}</div>}
            </div>
        </div>,
        document.body
    );
};

export default AppModal;
